/**
 * Calendar UI state (zustand) — view, focused date, calendar visibility, and
 * the change counter the persistence layer bumps so open views re-read
 * (boardsUiStore's bumpBoards precedent).
 *
 * `focusedDateMs` is stored as an epoch ms number (never a Date instance in
 * the store — zustand equality on Dates is by reference and every set would
 * re-render everything). It is deliberately NOT persisted: a calendar should
 * open on "around today", not on wherever the user browsed to last week.
 *
 * Sidebar prefs (view + visibility toggles) persist per ACCOUNT via
 * accountScopedPersist (PLAN §3.5; fix round 1, N2) — the certificateStore
 * wiring: account-scoped key + wholesale rehydrate on account switch so one
 * account's hidden calendars never leak into another's session.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  accountScopedStorage,
  rehydrateOnAccountChange,
} from '../../../services/accountScopedPersist';

export const CALENDAR_VIEWS = ['month', 'week', 'day', 'agenda'];
/** Toolbar scope filter over the event record's `calendar` field (§3.2):
 *  'personal' → calendar === 'personal', 'team' → calendar starts 'team:',
 *  'all' → both. In Phase 1 the Team side is legitimately empty (team events
 *  arrive in Phase 3) — the filter is wired for real, not dead UI. */
export const CALENDAR_FILTERS = ['all', 'personal', 'team'];

const calendarUiStorage = accountScopedStorage('calendar-ui');
const PERSISTED_DEFAULTS = { view: 'month', hiddenCalendars: {}, calendarFilter: 'all' };

export const useCalendarUiStore = create(
  persist(
    (set) => ({
      view: 'month',
      focusedDateMs: Date.now(),
      // Per-calendar visibility toggles by key ('team:<id>' in Phase 3).
      // KEPT (persisted) although Phase 1 has no per-calendar UI anymore —
      // fix round 3 replaced the sidebar checkboxes with the coarser toolbar
      // filter below; Phase 3's team calendar list re-surfaces these.
      hiddenCalendars: {},
      // Toolbar Personal / Team / All segmented filter (fix round 3).
      calendarFilter: 'all',
      eventsVersion: 0,

      setView: (view) => set({ view: CALENDAR_VIEWS.includes(view) ? view : 'month' }),
      setCalendarFilter: (calendarFilter) =>
        set({
          calendarFilter: CALENDAR_FILTERS.includes(calendarFilter) ? calendarFilter : 'all',
        }),
      setFocusedDate: (date) =>
        set({ focusedDateMs: date instanceof Date ? date.getTime() : Number(date) }),
      toggleCalendarVisible: (key) =>
        set((s) => ({
          hiddenCalendars: { ...s.hiddenCalendars, [key]: !s.hiddenCalendars[key] },
        })),
      bumpEvents: () => set((s) => ({ eventsVersion: s.eventsVersion + 1 })),
    }),
    {
      name: 'calendar-ui', // ignored by accountScopedStorage; key = apibooKey('calendar-ui')
      storage: createJSONStorage(() => calendarUiStorage),
      partialize: (state) => ({
        view: state.view,
        hiddenCalendars: state.hiddenCalendars,
        calendarFilter: state.calendarFilter,
      }),
    }
  )
);

rehydrateOnAccountChange(useCalendarUiStore, PERSISTED_DEFAULTS, calendarUiStorage);

/** Imperative bump for non-React callers (store writes, sync pulls). */
export function bumpCalendarEvents() {
  useCalendarUiStore.getState().bumpEvents();
}
